// TODO - Type 'any' needs to be fixed.
import { makeStyles } from "@mui/styles";
import { differenceInMinutes } from "date-fns";
import format from "date-fns/format";
import isAfter from "date-fns/isAfter";
import type { TFunction } from "i18next";
import * as Yup from "yup";

export const useStyles = makeStyles(() => ({
	cancelButton: {
		marginRight: "auto",
	},
	dialogContentRoot: {
		maxWidth: "70%",
	},
	formControlRoot: {
		width: "100%",
		"& .MuiFilledInput-root": {
			background: "#F8F8F8 !important",
		},
	},
	btnLabel: {
		position: "relative",
	},
	progressIndicator: {
		position: "absolute",
		left: "50%",
	},
	formRoot: {
		width: "100%",
	},
}));

const toLocalISOString = (date: Date): string => {
	const off = date.getTimezoneOffset();
	return new Date(
		date.getFullYear(),
		date.getMonth(),
		date.getDate(),
		date.getHours(),
		date.getMinutes() - off,
		date.getSeconds(),
		date.getMilliseconds()
	).toISOString();
};

export const setToNearestTime = (dateString: string): string => {
	const date = new Date(dateString);
	return toLocalISOString(date).slice(0, 16);
};

export const configureDatesOfTheWeek = (date: Date): Record<string, Date> => {
	let returnValue: Record<string, Date> = {};
	const daysArray: string[] = [
		"", // To move sunday from 0 to 7 position
		"monday",
		"tuesday",
		"wednesday",
		"thursday",
		"friday",
		"saturday",
		"sunday",
	];
	const currentDay = date.getDay() === 0 ? 7 : date.getDay();
	for (let i = 1; i < 8; i++) {
		let tempDate = new Date();
		let dayModification = 0;
		if (currentDay > i) {
			dayModification = -1 * (currentDay - i) * 24;
		} else if (i > currentDay) {
			dayModification = (i - currentDay) * 24;
		}
		tempDate = new Date(date.getTime() + dayModification * 60 * 60 * 1000);
		returnValue[daysArray[i]] = tempDate;
	}
	returnValue = {
		monday: returnValue.monday,
		tuesday: returnValue.tuesday,
		wednesday: returnValue.wednesday,
		thursday: returnValue.thursday,
		friday: returnValue.friday,
		saturday: returnValue.saturday,
		sunday: returnValue.sunday,
	};
	return returnValue;
};

export const getYupInitObject = (
	t: TFunction
): Record<string, Yup.AnySchema> => ({
	startTime: Yup.string()
		.required(t("scheduler_start-time-required"))
		.test(
			"is-greater",
			t("scheduler_start-time-greater-current"),
			function (value: string | undefined) {
				return value ? isAfter(new Date(value), new Date()) : !!value;
			}
		),
	endTime: Yup.string()
		.required(t("scheduler_end-time-required"))
		.test(
			"is-greater",
			t("scheduler_end-time-greater-start"),
			function (value) {
				const { startTime } = this.parent;
				return value ? isAfter(new Date(value), new Date(startTime)) : !!value;
			}
		),
	workCenterId: Yup.string().required(t("scheduler_work-center-required")),
	numberOfWorkersNeeded: Yup.number()
		.moreThan(0, t("scheduler_min-worker-required"))
		.lessThan(100000, t("scheduler_max-worker-required"))
		.integer(t("scheduler_must-be-integer"))
		.required(t("scheduler_workers-required")),
	shiftPremium: Yup.string(),
	shiftNote: Yup.string().max(250, t("scheduler_note-max-length")),
	isShiftAcceptanceCutoffEnabled: Yup.boolean(),
	minHoursBeforeShiftStartToAccept: Yup.number().when(
		"isShiftAcceptanceCutoffEnabled",
		{
			is: true,
			then: Yup.number()
				.required(t("scheduler_acceptance-cutoff-required"))
				.min(0.5, t("scheduler_acceptance-cutoff-range"))
				.max(168, t("scheduler_acceptance-cutoff-range")),
		}
	),
	isFlexiSignupEnabled: Yup.boolean(),
	minFlexiSignupHours: Yup.number().when("isFlexiSignupEnabled", {
		is: true,
		then: Yup.number()
			.required(t("scheduler_min-signup-duration-required"))
			.integer(t("scheduler_must-be-integer"))
			.min(1, ({ min }) =>
				t("scheduler_min-signup-duration-hours", {
					count: min,
					unit: min > 1 ? t("scheduler_hours-unit") : t("scheduler_hour-unit"),
				})
			)
			.test(
				"max-shift-duration",
				t("scheduler_min-signup-less-than-total"),
				function (value) {
					const { startTime, endTime } = this.parent;
					if (!startTime || !endTime || !value) return true;
					const shiftDurationHours = differenceInMinutes(
						new Date(endTime),
						new Date(startTime)
					) / 60;
					return (value ?? 0) < shiftDurationHours;
				}
			),
	}),
});

interface ShiftData {
	startDate: string;
	endDate: string;
	createdByName?: string;
  createdBy?: string | number;
	createdDate?: string;
	workCenterId: string;
	jobId: string;
	numberOfWorkersNeeded: number;
	shiftPremium?: string;
	skills?: { skillId: string; skillName: string }[];
	shiftSkills?: { skillId: string; skillName: string }[];
	id: string | number;
	isPublished: boolean;
	startDateTime: string;
	locationId: string;
	shiftNote: string;
	shiftPurposeId: number;
	shiftPurposeDetail?: string | null;
	isPartialSignupEnabled: boolean;
	isFlexiSignupEnabled: boolean;
	minFlexiSignupHours: number;
	isShiftAcceptanceCutoffEnabled: boolean;
	minHoursBeforeShiftStartToAccept: number;
	allowedWorkerTypeIds?: number[] | null;
}

type Skills = { id: string; name: string };

interface GetInitValuesParams {
	allShiftsData: any; // Adjust the type accordingly
	shiftIdForEditShift: number | null;
	currentShiftDetails: (
		allShiftsData: any,
		shiftIdForEditShift: number | null
	) => ShiftData;
	filterAllWorkcenters?: any;
}

export const getInitValues = ({
	allShiftsData,
	shiftIdForEditShift,
	currentShiftDetails,
	filterAllWorkcenters,
}: GetInitValuesParams): any => {
	if (allShiftsData) {
		const shiftData = currentShiftDetails(allShiftsData, shiftIdForEditShift);
    if (!shiftData) {
      return {
        startTime: "",
        endTime: "",
        createdByName: "",
        createdBy: "",
        createdDate: "",
        workCenterId: "",
        jobId: null,
        numberOfWorkersNeeded: 0,
        shiftPremium: "",
        shiftData: {},
        shiftDate: "",
        shiftPublished: false,
        shiftId: null,
        currentShift: {},
        shiftSkills: [],
        locationId: "",
        shiftPurposeId: 0,
        shiftPurposeDetail: "",
        shiftNote: "",
        allowPartialSignups: false,
        isFlexiSignupEnabled: false,
        minFlexiSignupHours: 1,
        isShiftAcceptanceCutoffEnabled: false,
        minHoursBeforeShiftStartToAccept: 0,
        allowedWorkerTypeIds: null,
        isUrgent: false,
      };
    }
		const mySkills = shiftData.skills || shiftData.shiftSkills;
		const shiftSkills = mySkills?.map((skill) => ({
			...skill,
			id: skill.skillId,
			name: skill.skillName,
		}));

		const locationId =
			filterAllWorkcenters &&
			filterAllWorkcenters.find(
				(wc: any) => wc.id.toString() === shiftData.workCenterId.toString()
			)?.locationId;

		const startTime = format(new Date(shiftData.startDate), "yyyy-MM-dd") +
      "T" +
      format(new Date(shiftData.startDate), "HH:mm");
		const endTime = format(new Date(shiftData.endDate), "yyyy-MM-dd") +
      "T" +
      format(new Date(shiftData.endDate), "HH:mm");
		const shiftNote = shiftData.shiftNote;
		//console.log("shiftData", shiftData, allShiftsData);
		return {
			startTime,
			endTime,
			createdByName: shiftData?.createdByName ? shiftData.createdByName : "",
			createdDate: shiftData?.createdDate ? shiftData.createdDate : "",
			workCenterId: shiftData.workCenterId.toString(),
			jobId: shiftData.jobId ? shiftData.jobId : null,
			numberOfWorkersNeeded: shiftData.numberOfWorkersNeeded,
			shiftPremium: shiftData?.shiftPremium ?? "",
			shiftData,
			shiftDate: shiftData.startDateTime,
			shiftPublished: shiftData?.isPublished,
			shiftId: shiftData.id,
			currentShift: shiftData,
			shiftSkills: shiftSkills ?? [],
			locationId: locationId && locationId,
			shiftPurposeId: shiftData.shiftPurposeId ? shiftData.shiftPurposeId : 0,
			shiftPurposeDetail: shiftData.shiftPurposeDetail ?? "",
			shiftNote,
			allowPartialSignups: shiftData.isPartialSignupEnabled,
			isFlexiSignupEnabled:
				(shiftData as any).isFlexiSignupEnabled ||
				(shiftData as any).IsFlexiSignupEnabled ||
				false,
			minFlexiSignupHours:
				(shiftData as any).minFlexiSignupHours ||
				(shiftData as any).MinFlexiSignupHours ||
				1,
			isShiftAcceptanceCutoffEnabled:
				(shiftData as any).isShiftAcceptanceCutoffEnabled ||
				(shiftData as any).IsShiftAcceptanceCutoffEnabled ||
				false,
			minHoursBeforeShiftStartToAccept:
				(shiftData as any).minHoursBeforeShiftStartToAccept ||
				(shiftData as any).MinHoursBeforeShiftStartToAccept ||
				0,
			allowedWorkerTypeIds: shiftData.allowedWorkerTypeIds ?? null,
			createdBy: shiftData?.createdBy ? shiftData.createdBy : "",
			isUrgent: (shiftData as any).isUrgent ?? false,
		};
	}
};

export const sanetizeDateTime = (dateTime: string): string => {
  // Check if date time is already in ISO format with GMT timezone
  if (dateTime.endsWith("Z")) {
    return dateTime;
  }
  // Convert local time to GMT time with ISO format
  return new Date(dateTime).toISOString();
}